import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Button, Spinner, Modal, Form, Badge } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { FaEdit, FaPlus, FaTrash, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';

export default function AdminCommunityManager() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // Deletion States
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [fallbackCategoryId, setFallbackCategoryId] = useState('');
  
  // Form State
  const [editId, setEditId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('forum');
  const [isPrivate, setIsPrivate] = useState(false);
  const [order, setOrder] = useState(0);

  useEffect(() => {
    fetchCategories();
    
    // Auto-poll to keep thread counts and categories updated without page refresh
    const intervalId = setInterval(() => {
      fetchCategories(false);
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, []);

  const fetchCategories = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/categories`);
      const data = await res.json();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error("Error fetching categories", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const openModal = (cat = null) => {
    if (cat) {
      setEditId(cat.id);
      setName(cat.name);
      setDescription(cat.description || '');
      setIcon(cat.icon || 'forum');
      setIsPrivate(cat.isPrivate || false);
      setOrder(cat.order || 0);
    } else {
      setEditId('');
      setName('');
      setDescription('');
      setIcon('forum');
      setIsPrivate(false);
      setOrder(0);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          id: editId, 
          name, 
          description, 
          icon, 
          isPrivate, 
          order: parseInt(order) 
        })
      });
      const data = await res.json();
      if (data.success) {
        fetchCategories();
        setShowModal(false);
      } else {
        alert(data.error || "Failed to save category");
      }
    } catch (error) {
      console.error("Error saving category", error);
      alert("Failed to save category.");
    } finally {
      setActionLoading(false);
    }
  };

  const initiateDelete = (cat) => {
    setCategoryToDelete(cat);
    setFallbackCategoryId('');
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      setActionLoading(true);
      const query = fallbackCategoryId ? `?fallbackCategoryId=${fallbackCategoryId}` : '';
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/categories/${categoryToDelete.id}${query}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setShowDeleteConfirm(false);
        setShowSuccess(true);
        fetchCategories();
      } else {
        alert(data.error || "Failed to delete category");
      }
    } catch (error) {
      console.error(error);
      alert("Error deleting category");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="py-4 community-admin-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Community Channels Manager</h2>
        <Button variant="primary" onClick={() => openModal()}>
          <FaPlus className="me-2" /> Add Category
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Order</th>
                <th>Name</th>
                <th>Description</th>
                <th>Access</th>
                <th>Threads</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length > 0 ? (
                categories.sort((a,b) => a.order - b.order).map(cat => (
                  <tr key={cat.id}>
                    <td>{cat.order}</td>
                    <td className="fw-bold">{cat.name}</td>
                    <td className="text-truncate" style={{ maxWidth: '300px' }}>{cat.description}</td>
                    <td>
                      {cat.isPrivate ? (
                        <Badge bg="danger">Private</Badge>
                      ) : (
                        <Badge bg="success">Public</Badge>
                      )}
                    </td>
                    <td>{cat.threadCount || 0}</td>
                    <td className="text-end">
                      <Button variant="outline-primary" size="sm" onClick={() => openModal(cat)} className="me-2">
                        <FaEdit />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => initiateDelete(cat)}>
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No categories found. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? 'Edit Category' : 'New Category'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control 
                type="text" 
                value={name} 
                onChange={e => setName(e.target.value)} 
                required 
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={3}
                value={description} 
                onChange={e => setDescription(e.target.value)} 
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Icon (FontAwesome)</Form.Label>
                  <Form.Select value={icon} onChange={e => setIcon(e.target.value)}>
                    <option value="forum">Forum (Default)</option>
                    <option value="wrench">Wrench (Maintenance)</option>
                    <option value="trophy">Trophy (Tournaments)</option>
                    <option value="anchor">Anchor (Boating)</option>
                    <option value="map">Map (Lakes)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control 
                    type="number" 
                    value={order} 
                    onChange={e => setOrder(e.target.value)} 
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check 
                type="switch"
                id="private-switch"
                label="Private Category (Requires special role/auth)"
                checked={isPrivate}
                onChange={e => setIsPrivate(e.target.checked)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" /> : 'Save Category'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteConfirm} 
        onHide={() => setShowDeleteConfirm(false)} 
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          <FaTrash style={{ fontSize: '3rem', color: '#dc3545' }} className="mb-4" />
          <h4 className="text-white mb-3">Delete this category?</h4>
          
          <p className="text-muted mb-4">
            Are you sure you want to delete the category <strong>{categoryToDelete?.name || 'this category'}</strong>?<br/>
            This action cannot be undone.
          </p>

          {categoryToDelete?.threadCount > 0 && (
            <div className="mb-4 p-3 rounded border border-warning text-start" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
              <p className="mb-3 text-warning">
                <FaExclamationCircle className="me-1 mb-1" /> 
                <strong style={{ fontSize: '1.1rem' }}>Warning:</strong> This category contains <strong style={{ fontSize: '1.1rem' }}>{categoryToDelete.threadCount}</strong> active threads.
              </p>
              <Form.Group>
                <Form.Label className="fw-bold small text-light opacity-75">Move existing threads to:</Form.Label>
                <Form.Select 
                  value={fallbackCategoryId} 
                  onChange={(e) => setFallbackCategoryId(e.target.value)}
                  className="bg-dark text-white border-secondary"
                >
                  <option value="">-- Select a fallback category --</option>
                  {categories.filter(c => c.id !== categoryToDelete.id).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          <div className="d-flex gap-3 justify-content-center">
            <Button variant="outline-light" onClick={() => setShowDeleteConfirm(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={confirmDelete} 
              disabled={actionLoading || (categoryToDelete?.threadCount > 0 && !fallbackCategoryId)}
            >
              {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaTrash className="me-2" />}
              Yes, Delete It
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Success Modal */}
      <Modal 
        show={showSuccess} 
        onHide={() => setShowSuccess(false)} 
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          <div className="mb-4">
            <FaCheckCircle style={{ fontSize: '4rem', color: '#198754' }} />
          </div>
          <h4 className="text-white mb-3">Successfully Deleted</h4>
          <p className="text-muted mb-4">
            The category has been permanently removed from the system.
          </p>
          <Button variant="outline-light" onClick={() => setShowSuccess(false)} className="px-4 py-2">
            Done
          </Button>
        </Modal.Body>
      </Modal>

    </div>
  );
}
